import React, { Dispatch, KeyboardEvent, SetStateAction, useEffect } from "react"
import {
  ShopperInsightsSearchHitCheckout,
  ShopperInsightsSearchHitSession
} from "lib/types/generated/graphql-types"
import { SessionGroup } from "./types"
import CustomerSessionGroupRow from "./CustomerSessionGroupRow"
import {
  CheckoutHeader,
  Container,
  Section,
  TableHeader
} from "./CustomerCheckoutSessionTable.styled"
import { CustomerOrders } from "../../classes/customer/CustomerOrders"
import esb from "elastic-builder"
import { useLazyQuery } from "@apollo/client"
import SEARCH_SHOPPER_INSIGHTS from "../../graphql/queries/shopperInsights/SearchShopperInsights"
import ErrorMessage from "../Ui/Messages/ErrorMessage"
import { useSelector } from "react-redux"
import { customerSelect } from "lib/store/services/customer/customerSelector"
import { ClearButton, InputWrapper, SearchButton } from "../../views/Customer/Customer.styled"
import { ReactComponent as SearchIcon } from "images/icons/magnifying-glass.svg"
import { ReactComponent as XmarkIcon } from "images/icons/xmark.svg"
import { NoticeBanner } from "../Ui/Messages/NoticeBanner"
import { useNavigate } from "react-router-dom"

type Props = {
  checkouts: ShopperInsightsSearchHitCheckout[]
  customerOrders: CustomerOrders
  customerEmail: string
  searchString: string
  setSearchString: Dispatch<SetStateAction<string>>
  email: string
}

const CustomerCheckoutSessionTable = ({
  checkouts,
  customerOrders,
  searchString,
  setSearchString,
  email
}: Props) => {
  const navigate = useNavigate()
  const customer = useSelector(customerSelect)

  const clearCustomer = () => {
    customerOrders.clearAll()
    setSearchString("")
    if (email) navigate(location.pathname, { replace: true, state: null })
  }

  const getShopperInsightsSearchVariables = (email: string) => ({
    variables: {
      query: JSON.stringify(esb.termQuery("shippingAddress.email", email.toLowerCase()).toJSON()),
      from: 0,
      size: 1000
    }
  })

  const [searchShopperInsights, { error: checkoutError, loading }] = useLazyQuery(
    SEARCH_SHOPPER_INSIGHTS,
    {
      onCompleted: (data) => {
        customerOrders.setEmail(searchString)
        const checkoutHits = data.searchShopperInsights.hits.filter(
          (hit: ShopperInsightsSearchHitCheckout) =>
            hit.__typename === "ShopperInsightsSearchHitCheckout"
        )
        const sessionHits = data.searchShopperInsights.hits.filter(
          (hit: ShopperInsightsSearchHitSession) =>
            hit.__typename === "ShopperInsightsSearchHitSession"
        )

        // Get unique session IDs from the found checkouts
        const sessionIds = [
          ...new Set(
            checkoutHits
              .map((checkout: ShopperInsightsSearchHitCheckout) => checkout.sessionId)
              .filter(Boolean)
          )
        ] as string[]

        // Set initial results in Redux
        customerOrders.setCheckouts(checkoutHits)
        customerOrders.setSessions(sessionHits)

        // If we have session IDs, search for all checkouts with those session IDs
        if (sessionIds.length > 0) {
          searchCheckoutsBySessionId({
            variables: {
              query: JSON.stringify(esb.termsQuery("sessionId", sessionIds).toJSON()),
              from: 0,
              size: 1000
            }
          })
        }
      }
    }
  )

  const [searchCheckoutsBySessionId] = useLazyQuery(SEARCH_SHOPPER_INSIGHTS, {
    onCompleted: (data) => {
      const additionalCheckouts = data.searchShopperInsights.hits.filter(
        (hit: ShopperInsightsSearchHitCheckout) =>
          hit.__typename === "ShopperInsightsSearchHitCheckout"
      )

      // Merge with existing checkouts, removing duplicates by ID
      const existingCheckouts = customer.checkouts
      const existingIds = new Set(existingCheckouts.map((c) => c.id))
      const newCheckouts = additionalCheckouts.filter(
        (c: ShopperInsightsSearchHitCheckout) => !existingIds.has(c.id)
      )
      const mergedCheckouts = [...existingCheckouts, ...newCheckouts]

      customerOrders.setCheckouts(mergedCheckouts)
    }
  })

  // Group checkouts by session ID
  const sessionGroups: SessionGroup[] = Object.values(
    checkouts.reduce(
      (groups, checkout) => {
        const sessionId = checkout.sessionId
        if (!groups[sessionId]) {
          groups[sessionId] = {
            sessionId,
            checkouts: [],
            totalAmount: 0,
            latestCreated: checkout.created
          }
        }
        groups[sessionId].checkouts.push(checkout)

        // Calculate total amount for the session
        const checkoutTotal =
          checkout.items?.reduce((sum, item) => sum + (item.totalPriceAmount || 0), 0) || 0
        groups[sessionId].totalAmount += checkoutTotal

        // Keep track of latest created date
        if (new Date(checkout.created) > new Date(groups[sessionId].latestCreated)) {
          groups[sessionId].latestCreated = checkout.created
        }

        return groups
      },
      {} as Record<string, SessionGroup>
    )
  )

  // Sort session groups by latest activity
  const sortedSessionGroups = sessionGroups.sort(
    (a, b) => new Date(b.latestCreated).getTime() - new Date(a.latestCreated).getTime()
  )

  useEffect(() => {
    if (email || customer.email) {
      searchShopperInsights(getShopperInsightsSearchVariables(searchString))
    }
  }, [email])

  const searchCustomerOrders = () => {
    searchShopperInsights(getShopperInsightsSearchVariables(searchString))
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      searchCustomerOrders()
    }
  }

  return (
    <Container className={loading ? "blur" : ""}>
      <InputWrapper>
        <input
          onChange={(e) => setSearchString(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={"Search email address"}
          value={searchString}
        />
        {searchString !== "" && (
          <ClearButton onClick={clearCustomer}>
            <XmarkIcon />
          </ClearButton>
        )}
        <SearchButton onClick={searchCustomerOrders} disabled={searchString === ""}>
          <SearchIcon />
        </SearchButton>
      </InputWrapper>
      {customer.email && (
        <>
          <NoticeBanner type="info">
            These are checkout insights showing sessions initiated by the customer. A checkout
            session represents customer activity and interest - it's not an indication of anything
            wrong.
            <br />
            This data provides valuable insights into customer behaviour and shopping patterns.
          </NoticeBanner>
          {checkoutError && <ErrorMessage message={"Error when fetching checkout sessions"} />}
          <TableHeader>
            <CheckoutHeader>Session</CheckoutHeader>
            <Section>Insights</Section>
            <Section className="hideOnMediumScreens">Store Group</Section>
            <Section className="hideOnMediumScreens">Store Market</Section>
            <Section className="hideOnSmallScreens">Checkout Total</Section>
            <Section>Status</Section>
          </TableHeader>
          {sortedSessionGroups.length === 0 && <p>- No insights found </p>}
          {sortedSessionGroups.map((sessionGroup) => (
            <CustomerSessionGroupRow
              sessionGroup={sessionGroup}
              key={sessionGroup.sessionId}
              customerOrders={customerOrders}
            />
          ))}
        </>
      )}
    </Container>
  )
}

export default CustomerCheckoutSessionTable
